import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useSupabaseQuery } from './useSupabase';
import { useAppStore } from '../stores/appStore';
import type { WAHASession, WAHAConfig } from '../types';
import type { Database } from '../lib/database.types';

type SessionInsert = Database['public']['Tables']['waha_sessions']['Insert'];
// Accept a partial config update merged with required org_id + base_url for upsert
type ConfigUpsert = Database['public']['Tables']['waha_config']['Insert'];

const MOCK_SESSIONS: WAHASession[] = [
  { id: '1', org_id: 'demo', session_name: 'sales-main', status: 'WORKING', phone_number: '+1 555-012-3456', push_name: 'Digitix Sales', engine: 'NOWEB', waha_instance_url: 'http://localhost:3000', created_at: new Date().toISOString(), last_seen_at: null },
  { id: '2', org_id: 'demo', session_name: 'sales-backup', status: 'WORKING', phone_number: '+1 555-078-9012', push_name: 'Digitix Outreach', engine: 'NOWEB', waha_instance_url: 'http://localhost:3000', created_at: new Date().toISOString(), last_seen_at: null },
];

const MOCK_CONFIG: WAHAConfig = {
  id: 'demo', org_id: 'demo', base_url: 'http://localhost:3000', api_key: null,
  engine: 'NOWEB', webhook_url: 'https://digitix.app/api/webhook',
  webhook_events: ['message', 'message.ack', 'session.status', 'presence.update'],
  send_limits: { window_start: '09:00', window_end: '18:00', messages_per_10min: 20, timezone: 'UTC' },
  created_at: new Date().toISOString(),
};

interface WAHAData {
  sessions: WAHASession[];
  wahaConfig: WAHAConfig | null;
}

interface WAHASessionInfo {
  name: string;
  status: string;
  me?: { id: string; pushName: string };
  engine?: { engine: string };
}

export function useWAHA(): {
  sessions: WAHASession[];
  wahaConfig: WAHAConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  saveConfig: (data: ConfigUpsert) => Promise<void>;
  createSession: (data: Omit<SessionInsert, 'org_id'>) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  syncFromWAHA: (baseUrl: string, apiKey: string | null) => Promise<WAHASessionInfo[]>;
} {
  const orgId = useAppStore(s => s.orgId);
  const [mutating, setMutating] = useState(false);

  const { data, loading, error, refetch } = useSupabaseQuery<WAHAData>(
    async () => {
      if (!isSupabaseConfigured) {
        return { data: { sessions: MOCK_SESSIONS, wahaConfig: MOCK_CONFIG }, error: null };
      }
      const [sessionsResult, configResult] = await Promise.all([
        supabase.from('waha_sessions').select('*'),
        supabase.from('waha_config').select('*').single(),
      ]);
      if (sessionsResult.error) return { data: null, error: sessionsResult.error };
      return {
        data: {
          sessions: (sessionsResult.data ?? []) as WAHASession[],
          wahaConfig: configResult.data as WAHAConfig | null,
        },
        error: null,
      };
    },
    []
  );

  const saveConfig = useCallback(
    async (configData: ConfigUpsert) => {
      if (!isSupabaseConfigured) { toast.info('Connect Supabase to save config'); return; }
      setMutating(true);
      try {
        const { error: err } = await supabase
          .from('waha_config')
          .upsert(configData, { onConflict: 'org_id' });
        if (err) throw err;
        toast.success('Config saved');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save config');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  const createSession = useCallback(
    async (sessionData: Omit<SessionInsert, 'org_id'>) => {
      if (!isSupabaseConfigured) { toast.info('Connect Supabase to create sessions'); return; }
      setMutating(true);
      try {
        const { error: err } = await supabase
          .from('waha_sessions')
          .insert({ ...sessionData, org_id: orgId! });
        if (err) throw err;
        toast.success('Session created');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create session');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  const deleteSession = useCallback(
    async (id: string) => {
      if (!isSupabaseConfigured) { toast.info('Connect Supabase to delete sessions'); return; }
      setMutating(true);
      try {
        const { error: err } = await supabase.from('waha_sessions').delete().eq('id', id);
        if (err) throw err;
        toast.success('Session deleted');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete session');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  const syncFromWAHA = useCallback(
    async (baseUrl: string, apiKey: string | null): Promise<WAHASessionInfo[]> => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (apiKey) headers['X-Api-Key'] = apiKey;
      const res = await fetch(`${baseUrl}/api/sessions`, { headers });
      if (!res.ok) throw new Error(`WAHA API error: ${res.status} ${res.statusText}`);
      const wahaSessions: WAHASessionInfo[] = await res.json();

      if (isSupabaseConfigured && orgId) {
        for (const s of wahaSessions) {
          const { data: existing } = await supabase
            .from('waha_sessions')
            .select('id')
            .eq('session_name', s.name)
            .eq('org_id', orgId)
            .maybeSingle();
          if (existing) {
            await supabase
              .from('waha_sessions')
              .update({ status: s.status, phone_number: s.me?.id?.replace(/@\w+\.us$/, '') ?? null, push_name: s.me?.pushName ?? null, last_seen_at: new Date().toISOString() })
              .eq('id', existing.id);
          } else {
            await supabase.from('waha_sessions').insert({
              org_id: orgId,
              session_name: s.name,
              status: s.status,
              phone_number: s.me?.id?.replace(/@\w+\.us$/, '') ?? null,
              push_name: s.me?.pushName ?? null,
              engine: (s.engine?.engine ?? 'NOWEB') as 'NOWEB' | 'WEBJS' | 'GOWS',
              waha_instance_url: baseUrl,
            });
          }
        }
        refetch();
      }
      return wahaSessions;
    },
    [orgId, refetch]
  );

  return {
    sessions: data?.sessions ?? (isSupabaseConfigured ? [] : MOCK_SESSIONS),
    wahaConfig: data?.wahaConfig ?? (isSupabaseConfigured ? null : MOCK_CONFIG),
    loading: loading || mutating,
    error,
    refetch,
    saveConfig,
    createSession,
    deleteSession,
    syncFromWAHA,
  };
}
